import React, { useEffect, useMemo, useRef, useState } from "react";
import MicRecorder from "mic-recorder-to-mp3";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, LinearProgress
} from "@material-ui/core";
import TranscriptsService from "../../../../../services/api/transcripts.service";

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - patientId: string
 *  - onJobStarted?: (jobName: string) => void  // parent can refresh list
 */
export default function VoiceRecorderDialog({
  open,
  onClose,
  patientId,
  onJobStarted,
}) {
  const [recorder] = useState(() => new MicRecorder({ bitRate: 128 }));
  const [permission, setPermission] = useState("unknown"); // "unknown" | "granted" | "denied"
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [startingJob, setStartingJob] = useState(false);
  const [etag, setEtag] = useState(null);

  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const pausedAtRef = useRef(null);

  // Ask for mic permission when opened
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setPermission("granted");
      } catch {
        setPermission("denied");
        console.warn("Microphone permission denied");
      }
    })();
  }, [open]);

  // Cleanup when closing
  useEffect(() => {
    if (open) return;
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    startedAtRef.current = null;
    pausedAtRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    setElapsedMs(0);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setBlob(null);
    setUploading(false);
    setStartingJob(false);
    setEtag(null);
  }, [open, blobUrl]);

  const fmt = (ms) => {
    const s = Math.floor(ms / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const sizeLabel = useMemo(() => {
    if (!blob) return "";
    const kb = blob.size / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(0)} KB`;
  }, [blob]);

  const tick = () => {
    if (!startedAtRef.current) return;
    setElapsedMs(Date.now() - startedAtRef.current);
    timerRef.current = requestAnimationFrame(tick);
  };

  const handleStart = async () => {
    if (permission !== "granted") {
      alert("Please allow microphone access to record.");
      return;
    }
    try {
      // reset previous recording
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlob(null);
      setBlobUrl(null);
      setEtag(null);
      setElapsedMs(0);

      await recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      startedAtRef.current = Date.now();
      timerRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.error(e);
      alert("Failed to start recording.");
    }
  };

  const handlePause = async () => {
    try {
      await recorder.pause();
      setIsPaused(true);
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
      pausedAtRef.current = Date.now();
    } catch(error) {
      alert("Unable to pause. " + error.message);
    }
  };

  const handleResume = async () => {
    try {
      await recorder.resume();
      setIsPaused(false);
      if (startedAtRef.current && pausedAtRef.current) {
        const pauseDelta = Date.now() - pausedAtRef.current;
        startedAtRef.current += pauseDelta; // maintain elapsed time
      }
      timerRef.current = requestAnimationFrame(tick);
    } catch {
      alert("Unable to resume.");
    }
  };

  const handleStop = async () => {
    try {
      const [, mp3] = await recorder.stop().getMp3();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
      startedAtRef.current = null;
      pausedAtRef.current = null;

      setBlob(mp3);
      const url = URL.createObjectURL(mp3);
      setBlobUrl(url);
    } catch (e) {
      console.error(e);
      alert("Failed to finalize recording.");
    }
  };

  const handleDiscard = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setEtag(null);
    setElapsedMs(0);
  };


  const uploadAndStart = async () => {
    if (!blob) {
      alert("Nothing to upload yet.");
      return;
    }
    setUploading(true);
    try {

      const { jobName, etag } = await TranscriptsService.uploadAndStartTranscript(patientId, blob);

      onJobStarted && onJobStarted(jobName);
      alert("Upload complete. Transcription job started.");
      onClose();
    } catch (e) {
      console.error(e);
      alert("Upload or job start failed. Check S3 CORS/headers & API reachability.");
    } finally {
      setUploading(false);
      setStartingJob(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Patient Conversation</DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="textSecondary">
            {isRecording ? "● Recording" : isPaused ? "Paused" : "Idle"}
          </Typography>
          {blob && <Typography variant="body2">Size: {sizeLabel}</Typography>}
        </Box>

        <Box mb={1}>
          <Typography variant="h5" style={{ fontFamily: "monospace" }}>
            {fmt(elapsedMs)}
          </Typography>
        </Box>

        {(isRecording || isPaused) && (
          <LinearProgress variant="indeterminate" style={{ marginBottom: 12 }} />
        )}

        <Box display="flex" flexWrap="wrap" gridGap={8} mb={blob ? 12 : 4}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleStart}
            disabled={isRecording || permission !== "granted"}
          >
            Start
          </Button>
          <Button
            variant="outlined"
            onClick={handlePause}
            disabled={!isRecording || isPaused}
          >
            Pause
          </Button>
          <Button
            variant="outlined"
            onClick={handleResume}
            disabled={!isRecording || !isPaused}
          >
            Resume
          </Button>
          <Button
            variant="contained"
            onClick={handleStop}
            disabled={!isRecording}
          >
            Stop
          </Button>
          <Button
            variant="text"
            onClick={handleDiscard}
            disabled={!blob}
          >
            Discard
          </Button>
        </Box>

        {blobUrl && (
          <Box
            style={{ border: "1px solid #e0e0e0", borderRadius: 6, padding: 12 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <audio src={blobUrl} controls style={{ width: "100%" }} />
            <Box mt={2} display="flex" gridGap={8}>
              <Button
                variant="contained"
                color="primary"
                onClick={uploadAndStart}
                disabled={uploading || startingJob}
              >
                {uploading || startingJob ? "Uploading…" : "Upload & Start Transcript"}
              </Button>
              <a href={blobUrl} download={`recording-${Date.now()}.mp3`}>
                <Button variant="outlined">Download MP3</Button>
              </a>
            </Box>
            {etag && (
              <Typography variant="caption" color="textSecondary">
                ETag: {etag}
              </Typography>
            )}
          </Box>
        )}

        {!blob && (
          <Box mt={2} color="#666">
            <Typography variant="subtitle2">Tips</Typography>
            <ul style={{ marginTop: 4 }}>
              <li>Click <b>Start</b> to begin, then <b>Pause/Resume</b> if needed.</li>
              <li>Click <b>Stop</b> to finalize, preview, then <b>Upload & Start</b>.</li>
            </ul>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <button className='btn btn-primary me-2 ' 
            onClick={onClose} disabled={uploading || startingJob}>
            Close
        </button>
      </DialogActions>
    </Dialog>
  );
}
